import { Clock, Sparkles, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTrialContext } from '@/contexts/TrialContext';
import { useTrialCountdown } from '@/hooks/use-trial-countdown';
import { useSubscriptionStatus } from '@/hooks/use-subscription-status';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';

interface FreeTrialBannerProps {
  onStartTrial?: () => void;
  onUpgrade?: () => void;
}

export const FreeTrialBanner = ({ onStartTrial, onUpgrade }: FreeTrialBannerProps) => {
  const { 
    trial, 
    hasActiveTrial, 
    hasExpiredTrial,
    isLoading: trialLoading 
  } = useTrialContext();
  const { 
    hasActiveSubscription, 
    subscriptionType,
    isLoading: subscriptionLoading 
  } = useSubscriptionStatus();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Use the countdown hook only when we have an active trial
  const { formattedTime, timeRemaining } = useTrialCountdown({
    expiresAt: trial?.expires_at || '',
    isActive: hasActiveTrial
  });
  
  const lifetimePassesLeft = 998;

  const handleUpgradeToLifetime = async () => {
    try {
      setIsProcessing(true);
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          priceId: 'price_1Q8QzHCFL931KD1u55LEohO0'
        }
      });

      if (error) throw error;
      if (!data?.url) throw new Error('No checkout URL received');

      window.location.href = data.url;
    } catch (error) {
      console.error('Error in lifetime upgrade flow:', error);
      toast({
        title: "Error",
        description: "There was a problem processing your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpgradeToMonthly = async () => {
    try {
      setIsProcessing(true);
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          priceId: 'price_1QhOtZCFL931KD1uLd6J00IU'
        }
      });

      if (error) throw error;
      if (!data?.url) throw new Error('No checkout URL received');

      window.location.href = data.url;
    } catch (error) {
      console.error('Error in monthly upgrade flow:', error);
      toast({
        title: "Error",
        description: "There was a problem processing your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Don't render anything while loading
  if (trialLoading || subscriptionLoading) {
    return null;
  }

  // Don't show any banner if user has active subscription
  if (hasActiveSubscription) {
    if (subscriptionType === 'lifetime') {
      return (
        <Card className="bg-gradient-to-r from-purple-50 to-yellow-50 border-purple-200 p-3 sm:p-4 mb-6 mx-auto max-w-4xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-full flex-shrink-0">
                <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-purple-800 text-sm sm:text-base">Thank You, Lifetime Member! 🎉</h3>
                <p className="text-xs sm:text-sm text-purple-600">
                  You have unlimited access to all CSO Agent features forever.
                </p>
              </div>
            </div>
          </div>
        </Card>
      );
    }

    // Don't show trial banners for Enterprise or Team subscribers
    if (subscriptionType === 'enterprise' || subscriptionType === 'team') {
      return null;
    }

    if (subscriptionType === 'monthly') {
      return (
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 p-3 sm:p-4 mb-6 mx-auto max-w-4xl">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
                <Crown className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-blue-800 text-sm sm:text-base">Upgrade to Lifetime Access</h3>
                <p className="text-xs sm:text-sm text-blue-600">
                  Only {lifetimePassesLeft} lifetime passes left! Get unlimited access forever.
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <Button 
                onClick={handleUpgradeToLifetime} 
                className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm px-3 sm:px-4 py-2 flex-shrink-0"
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Get Lifetime Access"}
              </Button>
            </div>
          </div>
        </Card>
      );
    }
  }

  // Show expired trial banner
  if (hasExpiredTrial) {
    return (
      <Card className="bg-gradient-to-r from-orange-50 to-red-50 border-orange-200 p-3 sm:p-4 mb-6 mx-auto max-w-4xl">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-orange-100 p-2 rounded-full flex-shrink-0">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-orange-800 text-sm sm:text-base">Trial Expired - Upgrade Now</h3>
              <p className="text-xs sm:text-sm text-orange-600">
                Your 24-hour free trial has ended. Upgrade to continue using CSO Agent.
              </p>
            </div>
          </div>
          <div className="flex gap-2 justify-center">
            <Button 
              onClick={handleUpgradeToMonthly} 
              className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4 py-2"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Monthly Plan"}
            </Button>
            <Button 
              onClick={handleUpgradeToLifetime} 
              className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm px-3 sm:px-4 py-2"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Lifetime Access"}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Show active trial banner - now uses the countdown hook
  if (hasActiveTrial && timeRemaining > 0) {
    return (
      <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200 p-3 sm:p-4 mb-6 mx-auto max-w-4xl">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-green-100 p-2 rounded-full flex-shrink-0">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-green-800 text-sm sm:text-base">Free Trial Active</h3>
              <p className="text-xs sm:text-sm text-green-600">
                Time remaining: <span className="font-mono font-bold">{formattedTime}</span>
              </p>
            </div>
          </div>
          <div className="flex gap-2 justify-center">
            <Button 
              onClick={handleUpgradeToMonthly} 
              className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4 py-2"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Monthly Plan"}
            </Button>
            <Button 
              onClick={handleUpgradeToLifetime} 
              className="bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm px-3 sm:px-4 py-2"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Lifetime Access"}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Only show "Start Free Trial" banner if no trial exists at all
  if (!trial) {
    return (
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 p-3 sm:p-4 mb-6 mx-auto max-w-4xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-full flex-shrink-0">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-blue-800 text-sm sm:text-base">Start Your Free Trial</h3>
              <p className="text-xs sm:text-sm text-blue-600">
                Get 24 hours of full access to all CSO Agent features.
              </p>
            </div>
          </div>
          <Button 
            onClick={onStartTrial} 
            className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4 py-2 w-full sm:w-auto"
          >
            Start Free Trial
          </Button>
        </div>
      </Card>
    );
  }

  return null;
};
